import React, { useState } from 'react';
import axios from 'axios';
import './dinMobileDisplay.css';

const DinMobileNumberDisplay = () => {
  const [dinNumber, setDinNumber] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usagePercent, setUsagePercent] = useState(null);

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow only numbers
    if (value === '' || /^\d+$/.test(value)) {
      setDinNumber(value);
      setError('');
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dinNumber.trim()) {
      setError('Please enter a DIN number');
      return;
    }

    if (dinNumber.length < 8) {
      setError('DIN number should be at least 8 digits');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);
    setMobileNumber('');

    try {
      const data = JSON.stringify({
        DIN_Number: dinNumber,
      });

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: 'https://api.codentechnologies.com/api/vcare/get-details',
        headers: {
          'Content-Type': 'application/json',
        },
        data: data,
      };

      const response = await axios.request(config);
      
      if (response.data?.success && response.data?.data?.data?.result?.mobileNumber) {
        const body = response.data.data.data;
        setMobileNumber(body.result.mobileNumber);
        // compute usage percent if available
        const currentCredit = body.currentCredit;
        const creditUsed = body.creditUsed;
        if (typeof currentCredit === 'number' && currentCredit > 0 && typeof creditUsed === 'number') {
          const pct = Math.round((creditUsed / currentCredit) * 10000) / 100; // two decimals
          setUsagePercent(pct);
        } else {
          setUsagePercent(null);
        }
        setSuccess(true);
      } else {
        setError('No mobile number found for this DIN number');
        setUsagePercent(null);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to retrieve mobile number. Please try again.'
      );
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDinNumber('');
    setMobileNumber('');
    setError('');
    setSuccess(false);
    setCopied(false);
    setUsagePercent(null);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(mobileNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="din-container">
      <div className="din-card">
        <div className="din-header">
          <h1 className="din-title">Retrieve Mobile Number</h1>
          <p className="din-subtitle">Enter your DIN number to get your registered mobile number</p>
        </div>

        <form onSubmit={handleSubmit} className="din-form">
          <div className="form-group">
            <label htmlFor="dinInput" className="form-label">
              DIN Number <span className="required">*</span>
            </label>
            <input
              type="text"
              id="dinInput"
              className={`form-input ${error && 'input-error'} ${success && 'input-success'}`}
              placeholder="Enter your 8-digit DIN number"
              value={dinNumber}
              onChange={handleInputChange}
              disabled={loading}
              maxLength="20"
            />
            <div className="input-hint">Example: 02055644</div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !dinNumber.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Searching...
                </>
              ) : (
                'Retrieve Mobile Number'
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="alert alert-error">
            <svg className="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && mobileNumber && (
          <div className="alert alert-success">
            <svg className="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Mobile number retrieved successfully!</span>
          </div>
        )}

        {success && mobileNumber && (
          <div className="result-section">
            <h2 className="result-title">Your Mobile Number</h2>
            <div className="mobile-number-display">
              <span className="mobile-icon">📱</span>
              <span className="mobile-number">{mobileNumber}</span>
              <button
                type="button"
                className={`copy-button ${copied ? 'copied' : ''}`}
                onClick={handleCopyToClipboard}
                title="Copy to clipboard"
              >
                <svg
                  className="copy-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                </svg>
                <span className="copy-text">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
            {usagePercent !== null && (
              <div className="usage-percent">
                <strong>Usage: {usagePercent}%</strong>
              </div>
            )}
            <p className="result-note">Please save this number for future reference</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DinMobileNumberDisplay;
